import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams, Link } from "react-router";
import { safeReturnTo } from "../lib/returnTo";
import { Button, Card, FormField, Input, PasswordInput, Stack, guardSubmit } from "@salesapp/ui";
import { ApiError } from "../lib/api";
import { useAuth, type OrgOption } from "../shell/AuthProvider";
import "./pages.css";
import { ThemeToggle } from "../shell/ThemeToggle";
import { billingApi } from "../lib/billingApi";

export const LOGIN_NETWORK_ISSUE = "Unable to log you in due to a network issue.";
export const LOGIN_LOCKOUT_MESSAGE = "Too many sign-in attempts";

const LOGIN_USER_MESSAGES = new Set([
  "Invalid email or password",
  LOGIN_LOCKOUT_MESSAGE,
  "Access from this network is not allowed.",
  "This organization can only be used from approved networks.",
  "SSO is not enabled for this email.",
]);

function resetPasswordHref(email: string): string {
  const trimmed = email.trim();
  return trimmed ? `/reset-password?email=${encodeURIComponent(trimmed)}` : "/reset-password";
}

export function loginFailureMessage(err: unknown): string {
  if (err instanceof ApiError && err.status >= 400 && err.status < 500 && LOGIN_USER_MESSAGES.has(err.message)) {
    return err.message;
  }
  return LOGIN_NETWORK_ISSUE;
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get("returnTo"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Populated only when the backend returns 300 "choose_organization" --
  // credentials were correct, but the user belongs to more than one org and
  // must pick which one to enter. org_id here always comes from THIS list,
  // never something the user types (guardrail: org_id is opaque/internal).
  const [orgChoices, setOrgChoices] = useState<OrgOption[] | null>(null);

  async function completeLogin(orgId?: string) {
    const outcome = await login(email, password, orgId);
    if (outcome.kind === "choose_organization") {
      setOrgChoices(outcome.organizations);
      return;
    }
    navigate(returnTo, { replace: true });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLocked(false);
    setSubmitting(true);
    try {
      await completeLogin();
    } catch (err) {
      const message = loginFailureMessage(err);
      setError(message);
      setLocked(message === LOGIN_LOCKOUT_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleChooseOrg(orgId: string) {
    setError(null);
    setSubmitting(true);
    try {
      await completeLogin(orgId);
    } catch (err) {
      setError(loginFailureMessage(err));
      setOrgChoices(null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page-frame">
      <ThemeToggle />
      {orgChoices ? (
        <Card padding="lg" className="login-card-wide">
          <Stack gap={5}>
            <Stack gap={2} align="center">
              <span style={{ fontSize: 32 }}>{"\u{1F3E2}"}</span>
              <h1 style={{ fontSize: "var(--font-size-xl)", margin: 0 }}>Choose your workspace</h1>
              <p style={{ color: "var(--color-text-muted)", margin: 0, fontSize: "var(--font-size-sm)", textAlign: "center" }}>
                {email} belongs to more than one organization. Pick which pipeline to open.
              </p>
            </Stack>

            {error && (
              <p style={{ color: "var(--color-danger)", margin: 0, fontSize: "var(--font-size-sm)", textAlign: "center" }}>
                {error}
              </p>
            )}

            <Stack gap={3}>
              {orgChoices.map((org) => (
                <Card
                  key={org.org_id}
                  interactive
                  padding="md"
                  onClick={() => {
                    if (!submitting) void handleChooseOrg(org.org_id);
                  }}
                  className="login-org-choice"
                  style={{ opacity: submitting ? 0.6 : 1 }}
                >
                  <span style={{ fontWeight: "var(--font-weight-medium)" }}>{org.display_name}</span>
                  <span aria-hidden style={{ color: "var(--color-primary)", fontSize: "var(--font-size-lg)" }}>
                    &rarr;
                  </span>
                </Card>
              ))}
            </Stack>

            <Button
              type="button"
              variant="ghost"
              fullWidth
              disabled={submitting}
              onClick={() => {
                setOrgChoices(null);
                setError(null);
              }}
            >
              Back to sign in
            </Button>
          </Stack>
        </Card>
      ) : (
        <Card padding="lg" className="login-card">
          <Stack gap={5}>
            <Stack gap={2} align="center">
              <a href="https://saleslox.com" target="_blank" rel="noopener noreferrer" className="login-brand-mark-link">
                <img src="/logo-mark.svg" alt="" className="login-brand-mark" />
              </a>
              <h1 className="login-brand-title">Saleslox</h1>
              <p className="login-brand-subtitle">Sign in to your pipeline</p>
            </Stack>

            {searchParams.get("passwordChanged") === "1" && (
              <p className="login-status" role="status">Password changed. Sign in with your new password.</p>
            )}
            {searchParams.get("reason") === "expired" && (
              <p className="login-status" role="status">Your session expired. Sign in again to continue.</p>
            )}
            {searchParams.get("reason") === "idle" && (
              <p className="login-status" role="status">
                You have been automatically logged out after 15 minutes of idle time.
              </p>
            )}
            {searchParams.get("reason") === "replaced" && (
              <p className="login-status" role="status">You were signed out because this account signed in on another computer.</p>
            )}
            {searchParams.get("sso_error") === "1" && (
              <p className="login-status" role="status">SSO sign-in could not be completed. Try again, or sign in with email and password.</p>
            )}
            <Stack as="form" gap={4} onSubmit={guardSubmit(handleSubmit)}>
              <FormField label="Email" htmlFor="login-email" required>
                <Input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </FormField>
              <FormField label="Password" htmlFor="login-password" required error={error ?? undefined}>
                <PasswordInput
                  id="login-password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </FormField>
              <Button type="submit" fullWidth disabled={submitting}>
                {submitting ? "Signing in..." : "Sign in"}
              </Button>
              {locked ? (
                <Stack gap={2} align="center">
                  <p className="login-status">
                    Reset your password to get a one-time setup link by email.
                  </p>
                  <Link className="login-reset-link" to={resetPasswordHref(email)}>
                    Reset password
                  </Link>
                </Stack>
              ) : (
                <p className="login-brand-subtitle">
                  <Link to={resetPasswordHref(email)}>Forgot password?</Link>
                </p>
              )}
              <Button
                type="button"
                variant="secondary"
                fullWidth
                disabled={!email || submitting}
                onClick={() => {
                  setError(null);
                  void billingApi.ssoStart(email).then((result) => {
                    if (result.mock && result.nonce) {
                      navigate(`/sso/mock?nonce=${encodeURIComponent(result.nonce)}&org=${encodeURIComponent(result.org_name)}`);
                      return;
                    }
                    const target = result.authorization_url || result.redirect_url;
                    if (target && (target.startsWith("https://") || target.startsWith("http://localhost") || target.startsWith("http://127.0.0.1"))) {
                      window.location.assign(target);
                      return;
                    }
                    setError("SSO is not enabled for this email.");
                  }).catch((err) => {
                    setError(loginFailureMessage(err));
                  });
                }}
              >
                Continue with SSO
              </Button>
              <p className="login-brand-subtitle">New organization? <a href="https://saleslox.com/signup">Start a trial</a></p>
              <p className="login-legal-notice">
                By logging in you accept the{" "}
                <a href="https://saleslox.com/terms" target="_blank" rel="noopener noreferrer">
                  Terms and Conditions
                </a>{" "}
                and{" "}
                <a href="https://saleslox.com/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Statement
                </a>
                .
              </p>
            </Stack>
          </Stack>
        </Card>
      )}
    </div>
  );
}
